/**
 * Analytics context: captures UTM params + referrer on first landing
 * and re-attaches them to every event in the session via a dataLayer
 * context push.
 *
 * Read once at app boot. Call attachSessionContext() before instrumenting
 * any component, so GTM's Data Layer Variables can pick up the globals.
 */

import { track } from './dataLayer';
import { isExternalTrackingBlockedPath } from './productionTracking';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const STORAGE_KEY = 'rxr_analytics_context';

const safeStorage = {
  get: () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch {
      return null;
    }
  },
  set: (v) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
    } catch {
      // ignore quota
    }
  },
};

const readUtmFromUrl = () => {
  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) out[k] = v;
    });
    return out;
  } catch {
    return {};
  }
};

const readGclid = () => {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('gclid');
  } catch {
    return null;
  }
};

const readFbclid = () => {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('fbclid');
  } catch {
    return null;
  }
};

// Environment tag + client tag are derived from build-time env and runtime UA.
// Always re-attached on every context read, even when returning cached context,
// so we can't drift if env/build changes between visits.
const getEnvTags = () => ({
  app_env: (process.env.REACT_APP_ENV || (process.env.NODE_ENV === 'production' ? 'production' : 'local')),
  gtm_id: process.env.REACT_APP_GTM_ID || null,
  client: (typeof window !== 'undefined' && window.isNativeApp)
    ? (/(iPhone|iPad|iPod)/.test(typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '') ? 'mobile_ios' : 'mobile_android')
    : 'web',
});

/**
 * Build the session's analytics context: UTM + referrer + click ids + env tags.
 * Persists on first landing; subsequent calls return the persisted context
 * unless URL has fresh UTM params (re-attributes on new campaign click).
 * Env tags (app_env, gtm_id, client) are ALWAYS re-attached so cached contexts
 * from older builds can't return stale values.
 */
export const getOrInitSessionContext = () => {
  if (isExternalTrackingBlockedPath()) return {};
  const existing = safeStorage.get();
  const freshUtm = readUtmFromUrl();
  const gclid = readGclid();
  const fbclid = readFbclid();

  const hasFreshCampaign =
    Object.keys(freshUtm).length > 0 || gclid || fbclid;

  if (existing && !hasFreshCampaign) {
    const merged = { ...existing, ...getEnvTags() };
    safeStorage.set(merged);
    return merged;
  }

  const context = {
    ...(existing || {}),
    ...freshUtm,
    ...(gclid ? { gclid } : {}),
    ...(fbclid ? { fbclid } : {}),
    referrer_path: typeof document !== 'undefined' ? document.referrer || null : null,
    landing_path: typeof window !== 'undefined' ? window.location.pathname : null,
    landing_ts: existing?.landing_ts || Date.now(),
    ...getEnvTags(),
  };

  // Normalize: drop empty strings
  Object.keys(context).forEach((k) => {
    if (context[k] === '' || context[k] === undefined) delete context[k];
  });

  safeStorage.set(context);
  return context;
};

/**
 * Push session context to the dataLayer so GTM's DLV - * variables pick it up
 * on every subsequent event without the caller having to repeat it.
 * Call once at app boot, and after every navigation if you want SPA re-attribution.
 */
export const attachSessionContext = () => {
  if (isExternalTrackingBlockedPath()) return {};
  const ctx = getOrInitSessionContext();
  track('session_context_attached', ctx);
  return ctx;
};

export const clearSessionContext = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Read the current context without mutating.
 * Useful for attaching UTM to registration_started etc.
 */
export const getSessionContext = () => {
  return safeStorage.get() || {};
};
